import { Component, inject, Injector, OnInit, OnDestroy, OnChanges, SimpleChanges, signal, Input, effect, computed, ViewChild, output, untracked, ProviderToken } from '@angular/core';
import { CONTROL_SYSTEM_LABEL, controlSystemDisplayName, deviceDisplayName, linkedDeviceDisplayName, locationDisplayName, locationTypeLabel } from '@/cloud/services/device-display';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Subscription } from 'rxjs';
import { TableModule } from 'primeng/table';
import { BadgeModule } from 'primeng/badge';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { ContextMenuModule } from 'primeng/contextmenu';
import { DialogModule } from 'primeng/dialog';
import { MessageModule } from 'primeng/message';
import { TabsModule } from 'primeng/tabs';
import { CloudUiControl } from '@/cloud/pages/device/ui-control/cloud-ui-control';
import { RoomContentComponent } from '../room-content/room-content.component';
import { CloudApiService } from '../../services/cloud-api.service';
import { MessageService, MenuItem } from 'primeng/api';
import { InfoWidgetComponent } from '@/shared/info-widget/info-widget.component';
import { LocationPickerComponent } from '../location-picker/location-picker.component';
import { ContextMenu } from 'primeng/contextmenu';
import { StatusBadgeComponent } from '@/shared/ui/status-badge/status-badge.component';
import { EmptyStateComponent } from '@/shared/ui/empty-state/empty-state.component';
import { LoadingSkeletonComponent } from '@/shared/ui/loading-skeleton/loading-skeleton.component';
import { SectionHeaderComponent } from '@/shared/ui/section-header/section-header.component';
import { deriveDeviceStatus, deriveLinkedDeviceStatus, linkedDeviceProblem } from '@/cloud/models/device-status';
import { StatusDescriptor } from '@/shared/ui/status.model';
import { TranslationService } from '@/layout/service/translation.service';
import { LocationInsightsComponent, availableInsightPanels } from '@/cloud/insights/location-insights/location-insights.component';
import { INSIGHT_PANELS, INSIGHT_TABS, InsightLinkedDevice, InsightTab, InsightTabDef } from '@/cloud/insights/insight-panel';
import { CloudFeedbackService } from '@/cloud/insights/cloud-feedback.service';
import { CloudMetricsService } from '@/cloud/insights/cloud-metrics.service';
import { OccupancyKpis, occupancyKpis } from '@/cloud/insights/panels/occupancy-insight-panel.component';
import { starGlyphs } from '@/cloud/models/feedback.models';
import { LocationDto } from '@/cloud/models/tree.models';
import { RoomUsageState, effectiveCapacity, isRoomType, roomStatus } from '@/cloud/models/room-usage.models';
import { RoomUsageCardComponent } from '../room-usage/room-usage-card.component';
import { RoomProfileDialogComponent, RoomProfileTarget } from '../room-usage/room-profile-dialog.component';
import { LocationSettingsComponent } from '../location-settings/location-settings.component';
import { translateCount } from '@/layout/service/translate-count';
import { CreateIssueDialogComponent } from '../create-issue-dialog/create-issue-dialog.component';
import { IssueListItem, IssueRepositoryFailure, RoomIssuesResponse, isRoomLabel, issueRoute, labelTextColor } from '@/cloud/models/github.models';
import { repositoryFailureText } from '@/cloud/pages/issues/issues';

@Component({
  selector: 'app-location-details',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    TableModule,
    BadgeModule,
    ButtonModule,
    TagModule,
    TooltipModule,
    ContextMenuModule,
    DialogModule,
    MessageModule,
    InfoWidgetComponent,
    LocationPickerComponent,
    StatusBadgeComponent,
    EmptyStateComponent,
    LoadingSkeletonComponent,
    SectionHeaderComponent,
    TabsModule,
    CloudUiControl,
    RoomContentComponent,
    LocationInsightsComponent,
    RoomUsageCardComponent,
    RoomProfileDialogComponent,
    LocationSettingsComponent,
    CreateIssueDialogComponent
  ],
  template: `
    <!-- Loading State -->
    @if (loading()) {
      <app-loading-skeleton preset="detail" [count]="2" label="Loading location" />
    }
    @if (!loading() && loadError()) {
      <app-empty-state icon="fa fa-circle-exclamation" tone="error" [title]="t()('dashboard.locationLoadError')" [description]="t()('dashboard.loadErrorDetail')" />
    }

    <!-- Location Info -->
    <div *ngIf="!loading() && location()" class="location-details-page">
      <header class="room-header">
        <span class="room-symbol" aria-hidden="true"><i class="fa fa-door-open"></i></span>
        <div class="room-heading">
          <span class="room-eyebrow">{{ typeLabel() }}</span>
          <h1>{{ locationName() }}</h1>
        </div>
        @if (parentDevice()) {
          <app-status-badge [status]="parentStatus()" size="sm" />
        }
        @if (canManageIssues()) {
          <p-button class="room-report-issue" [label]="t()('issues.reportIssue')" icon="pi pi-github" [outlined]="true" size="small" (onClick)="openReportIssue()" />
        }
      </header>
      <p-tabs class="room-tabs" [value]="activeTab()" (valueChange)="changeTab($event)">
        <p-tablist>
          <p-tab value="overview"
            ><i class="pi pi-home" aria-hidden="true"></i><span class="tab-label">{{ t()('room.overview') }}</span></p-tab
          >
          <p-tab value="ui-control"
            ><i class="pi pi-sliders-h" aria-hidden="true"></i><span class="tab-label">{{ t()('room.uiControl') }}</span></p-tab
          >
          <p-tab value="devices"
            ><i class="pi pi-desktop" aria-hidden="true"></i><span class="tab-label">{{ t()('room.devices') }}</span
            ><span class="room-device-count">{{ equipmentSummary().total }}</span></p-tab
          >
          @for (tab of visibleInsightTabs(); track tab.id) {
            <p-tab [value]="tab.id"
              ><i [class]="tab.icon" aria-hidden="true"></i><span class="tab-label">{{ t()(tab.labelKey) }}</span></p-tab
            >
          }
          <p-tab value="documents"
            ><i class="pi pi-folder-open" aria-hidden="true"></i><span class="tab-label">{{ t()('room.documents') }}</span></p-tab
          >
          <p-tab value="notes"
            ><i class="pi pi-comments" aria-hidden="true"></i><span class="tab-label">{{ t()('room.notes') }}</span></p-tab
          >
          <p-tab value="settings"
            ><i class="pi pi-cog" aria-hidden="true"></i><span class="tab-label">{{ t()('locationSettings.tab') }}</span></p-tab
          >
        </p-tablist>
        <p-tabpanels>
          <p-tabpanel value="overview">
            @if (activeTab() === 'overview') {
              @if (emptyLocation()) {
                <app-empty-state class="location-empty" role="status" icon="fa fa-seedling" [spacious]="true" [title]="t()('dashboard.emptyLocation')" [description]="t()('dashboard.emptyLocationDetail')" />
              } @else {
                <!-- Location Details Grid with Info Widgets -->
                <div class="telemetry-grid">
                  <app-info-widget [title]="typeLabel() + ' Details'" icon="stream">
                    <div class="room-details-content">
                      <div class="room-name-section">
                        <p class="value-text">{{ locationName() }}</p>
                        @if (location()!.code) {
                          <p class="value-text-small code-text">{{ location()!.code }}</p>
                        }
                        <p class="value-text-tiny id-text">ID: {{ location()!.uniqueId || '-' }}</p>
                      </div>

                      @if (isRoom()) {
                        <div class="room-profile-section">
                          <div class="room-profile-item">
                            <span class="date-label"><i class="pi pi-tag" aria-hidden="true"></i> {{ t()('roomProfile.type') }}</span>
                            <span class="date-value" [class.room-profile-unset]="!roomProfile().roomType">{{ t()(roomProfile().roomType ? 'roomType.' + roomProfile().roomType : 'roomType.notSet') }}</span>
                          </div>
                          <div class="room-profile-item">
                            <span class="date-label"><i class="fa fa-person" aria-hidden="true"></i> {{ t()('roomCapacity.label') }}</span>
                            <span class="date-value" [class.room-profile-unset]="roomProfile().capacity === null">{{ roomProfile().capacity === null ? t()('roomCapacity.notSet') : roomSeats() }}</span>
                          </div>
                          @if (canManageLocations()) {
                            <p-button
                              class="room-profile-edit"
                              [label]="t()(roomProfile().cloudCapacity === null ? 'roomCapacity.set' : 'roomProfile.edit')"
                              icon="pi pi-pencil"
                              size="small"
                              [text]="true"
                              (onClick)="openRoomProfile()"
                            />
                          }
                        </div>
                      }

                      <div class="room-dates-section">
                        @if (location()!.createdUtc) {
                          <div class="date-item">
                            <span class="date-label">
                              <i class="pi pi-calendar-plus"></i>
                              Created
                            </span>
                            <span class="date-value">{{ formatDate(location()!.createdUtc) }}</span>
                          </div>
                        }
                        @if (location()!.updatedUtc) {
                          <div class="date-item">
                            <span class="date-label">
                              <i class="pi pi-history"></i>
                              Updated
                            </span>
                            <span class="date-value">{{ formatDate(location()!.updatedUtc) }}</span>
                          </div>
                        }
                      </div>
                    </div>
                  </app-info-widget>

                  <!-- Control System (single parent) -->
                  <app-info-widget *ngIf="parentDevice()" [title]="controlSystemLabel" icon="microchip">
                    <div class="cs-box">
                      <div class="cs-header">
                        <a [routerLink]="['/device', parentDevice()!.id]" class="device-link">
                          {{ parentDeviceName() }}
                        </a>
                        <app-status-badge class="cs-status" [status]="parentStatus()" size="sm" />
                      </div>
                      @if (parentDevice(); as system) {
                        <dl class="cs-facts">
                          @if (system.model) {
                            <div class="cs-fact">
                              <dt><i class="pi pi-server" aria-hidden="true"></i>Model</dt>
                              <dd>{{ system.model }}</dd>
                            </div>
                          }
                          @if (system.firmwareVersion) {
                            <div class="cs-fact">
                              <dt><i class="pi pi-microchip" aria-hidden="true"></i>Firmware</dt>
                              <dd>{{ system.firmwareVersion }}</dd>
                            </div>
                          }
                          @if (system.packageNamespace || system.version) {
                            <div class="cs-fact">
                              <dt><i class="pi pi-code" aria-hidden="true"></i>Program</dt>
                              <dd>
                                @if (system.packageNamespace) {
                                  <code>{{ system.packageNamespace }}</code>
                                }
                                @if (system.version) {
                                  <span class="cs-fact__secondary">v{{ system.version }}</span>
                                }
                              </dd>
                            </div>
                          }
                          @if (system.ipAddress || system.hostName) {
                            <div class="cs-fact">
                              <dt><i class="pi pi-wifi" aria-hidden="true"></i>Network</dt>
                              <dd>
                                @if (system.ipAddress) {
                                  <code>{{ system.ipAddress }}</code>
                                }
                                @if (system.hostName) {
                                  <span class="cs-fact__secondary">{{ system.hostName }}</span>
                                }
                              </dd>
                            </div>
                          }
                        </dl>
                      }
                      <div class="cs-meta">
                        <span class="meta-item" pTooltip="Uptime">
                          <i class="pi pi-clock"></i>
                          {{ formatUptime(parentDevice()!.uptimeSeconds) }}
                        </span>
                        <span class="meta-item" [pTooltip]="'Last check-in ' + formatDate(parentDevice()!.lastCheckInUtc)">
                          <i class="pi pi-history"></i>
                          {{ formatRelativeTime(parentDevice()!.lastCheckInUtc, now()) }}
                        </span>
                      </div>
                    </div>
                  </app-info-widget>

                  @if (roomUsage(); as usage) {
                    <app-info-widget class="room-usage-widget" [title]="t()('roomUsage.title')" icon="users">
                      <app-room-usage-card [usage]="usage" [stale]="usageStale()" [now]="now()" [capacity]="roomProfile().capacity" [canManage]="canManageLocations()" (editProfile)="openRoomProfile()" />
                    </app-info-widget>
                  }

                  @if (canViewIssues()) {
                    <app-info-widget class="room-issues-widget" [title]="t()('issues.roomIssues')" icon="circle-exclamation">
                      <div class="room-issues">
                        @if (roomIssuesLoading() && !roomIssues()) {
                          <app-loading-skeleton preset="list" [count]="2" [label]="t()('issues.roomIssues')" />
                        } @else if (openRoomIssues().length === 0) {
                          <p class="room-issues__empty">{{ t()('issues.roomNoIssues') }}</p>
                          <small class="room-issues__hint">{{ t()('issues.roomNoIssuesDetail') }}</small>
                        } @else {
                          <ul class="room-issues__list" role="list">
                            @for (issue of openRoomIssues().slice(0, 5); track issue.htmlUrl) {
                              <li class="room-issue">
                                <i class="pi pi-circle room-issue__state" aria-hidden="true"></i>
                                <div class="room-issue__body">
                                  <a class="room-issue__title" [routerLink]="issueLink(issue)">{{ issue.title }}</a>
                                  <small class="room-issue__meta">
                                    <span>#{{ issue.number }}</span>
                                    @for (label of visibleIssueLabels(issue); track label.name) {
                                      <span class="room-issue__label" [style.background]="'#' + (label.color || 'ededed')" [style.color]="issueLabelTextColor(label.color)">{{ label.name }}</span>
                                    }
                                  </small>
                                </div>
                              </li>
                            }
                          </ul>
                        }
                        @for (failure of roomIssueFailures(); track failure.repository) {
                          <small class="room-issues__hint room-issues__hint--warn"><i class="pi pi-exclamation-triangle" aria-hidden="true"></i> {{ roomIssueFailureText(failure) }}</small>
                        }
                        <div class="room-issues__footer">
                          @if (openRoomIssues().length > 0) {
                            <span class="room-issues__count">{{ t()('issues.roomIssuesCount').replace('{n}', '' + openRoomIssues().length) }}</span>
                          }
                          <a class="room-issues__all" [routerLink]="['/issues']" [queryParams]="{ locationId: location()!.id }">{{ t()('issues.viewAll') }} <i class="pi pi-arrow-right" aria-hidden="true"></i></a>
                        </div>
                      </div>
                    </app-info-widget>
                  }
                </div>
                <!-- Small insights: one card per area, each opening its tab. -->
                <div class="room-snippets">
                  <article class="room-snippet room-snippet--devices">
                    <span class="room-eyebrow">{{ t()('room.devices') }}</span>
                    <p class="room-snippet__value">
                      <strong>{{ equipmentSummary().total }}</strong><span>{{ t()('room.equipmentInRoom') }}</span>
                    </p>
                    @if (equipmentSummary().total > 0) {
                      <p class="room-snippet__caption">
                        <span class="room-snippet__stat"><i class="pi pi-check-circle text-status-online" aria-hidden="true"></i>{{ equipmentSummary().online }} {{ t()('room.online') }}</span>
                        @if (equipmentSummary().attention > 0) {
                          <span class="room-snippet__stat"><i class="pi pi-exclamation-circle text-status-offline" aria-hidden="true"></i>{{ equipmentSummary().attention }} {{ t()('room.needsAttention') }}</span>
                        }
                      </p>
                    } @else {
                      <p class="room-snippet__caption">{{ t()('room.noDevices') }}</p>
                    }
                    <button type="button" class="room-snippet__link" (click)="changeTab('devices')">{{ t()('room.viewDevices') }} <i class="pi pi-arrow-right" aria-hidden="true"></i></button>
                  </article>
                  @for (tab of availableInsightTabs(); track tab.id) {
                    <article class="room-snippet" [attr.data-tab]="tab.id">
                      <span class="room-eyebrow">{{ t()(tab.labelKey) }}</span>
                      @switch (tab.id) {
                        @case ('usage') {
                          @if (overviewUsage() === undefined) {
                            <p class="room-snippet__value room-snippet__value--pending" aria-busy="true">…</p>
                          } @else if (overviewUsage()?.utilisationPct != null) {
                            <p class="room-snippet__value">
                              <strong>{{ overviewUsage()!.utilisationPct | number: '1.0-0' }}</strong><span>%</span>
                            </p>
                            <p class="room-snippet__caption">
                              <span>{{ t()('occupancy.utilisationCaption') }} · {{ t()('room.snippet.last7') }}</span>
                              @if (overviewUsage()!.bookedPct != null) {
                                <span>{{ t()('room.snippet.booked').replace('{pct}', (overviewUsage()!.bookedPct | number: '1.0-0') ?? '') }}</span>
                              }
                            </p>
                          } @else {
                            <p class="room-snippet__value"><strong>{{ t()('roomStatus.' + liveRoomStatus()) }}</strong></p>
                            <p class="room-snippet__caption">{{ t()('room.snippet.noUsageYet') }}</p>
                          }
                        }
                        @case ('power') {
                          @if (powerNowWatts() != null) {
                            <p class="room-snippet__value">
                              <strong>{{ powerNowWatts() | number: '1.0-0' }}</strong><span>W</span>
                            </p>
                            <p class="room-snippet__caption">{{ t()('insights.powerNow') }} · {{ powerDeviceCount() }}</p>
                          } @else {
                            <p class="room-snippet__value"><strong>—</strong></p>
                            <p class="room-snippet__caption">{{ t()('room.snippet.noPowerReading') }}</p>
                          }
                        }
                        @case ('feedback') {
                          @if (overviewFeedback() === undefined) {
                            <p class="room-snippet__value room-snippet__value--pending" aria-busy="true">…</p>
                          } @else if (overviewFeedback()?.average != null) {
                            <p class="room-snippet__value">
                              <strong>{{ overviewFeedback()!.average | number: '1.0-1' }}</strong><span>{{ t()('feedback.outOfFive') }}</span>
                              <span class="room-snippet__stars" aria-hidden="true">{{ feedbackStars() }}</span>
                            </p>
                            <p class="room-snippet__caption">{{ feedbackResponses() }} · {{ t()('room.snippet.last30') }}</p>
                          } @else {
                            <p class="room-snippet__value"><strong>—</strong></p>
                            <p class="room-snippet__caption">{{ t()('room.snippet.noFeedback') }}</p>
                          }
                        }
                      }
                      <button type="button" class="room-snippet__link" (click)="changeTab(tab.id)">{{ t()(tab.labelKey + '.open') }} <i class="pi pi-arrow-right" aria-hidden="true"></i></button>
                    </article>
                  }
                </div>
              }
            }
          </p-tabpanel>
          @for (tab of insightTabs; track tab.id) {
            <p-tabpanel [value]="tab.id">
              @if (activeTab() === tab.id && location()) {
                @if (roomLinkedDevices()) {
                  <app-location-insights [tab]="tab.id" [locationId]="location()!.id" [namespaceId]="location()!.appNamespaceId" [linkedDevices]="roomLinkedDevices()!" [location]="currentLocationFromArray() ?? location()" [usageStale]="usageStale()" />
                } @else {
                  <app-loading-skeleton preset="tiles" />
                }
              }
            </p-tabpanel>
          }
          <p-tabpanel value="ui-control">
            @if (activeTab() === 'ui-control') {
              @if (controlSystemId() && location()!.uniqueId) {
                <app-cloud-ui-control [deviceId]="controlSystemId()" [roomUniqueId]="location()!.uniqueId" [returnToRoom]="{ namespaceId: location()!.appNamespaceId, locationId: location()!.id }" />
              } @else {
                <app-empty-state icon="pi pi-sliders-h" [title]="t()('room.controlUnavailable')" [description]="t()('room.noController')" />
              }
            }
          </p-tabpanel>
          <p-tabpanel value="devices">
            @if (activeTab() === 'devices') {
              @if (equipmentSummary().total > 0) {
                <div class="room-equipment-health">
                  <span
                    ><i class="pi pi-check-circle text-status-online" aria-hidden="true"></i><strong>{{ equipmentSummary().online }}</strong> {{ t()('room.online') }}</span
                  >
                  <span
                    ><i class="pi pi-exclamation-circle text-status-offline" aria-hidden="true"></i><strong>{{ equipmentSummary().attention }}</strong> {{ t()('room.needsAttention') }}</span
                  >
                </div>
              }

              <!-- Devices (peripherals) in this location -->
              <div *ngIf="location()!.linkedDevices?.length > 0" class="devices-card">
                <app-section-header title="Devices" [subtitle]="'Peripherals reported by the ' + controlSystemLabel.toLowerCase() + ' in this ' + typeLabel().toLowerCase()" [count]="location()!.linkedDevices.length" />
                <p-table
                  [value]="location()!.linkedDevices"
                  dataKey="id"
                  class="p-datatable-striped"
                  [tableStyleClass]="'w-full'"
                  [paginator]="true"
                  [rows]="10"
                  [rowsPerPageOptions]="[10, 25, 50]"
                  [stateStorage]="'session'"
                  [stateKey]="linkedDevicesTableStateKey()"
                  [stripedRows]="true"
                >
                  <ng-template #header>
                    <tr>
                      <th pSortableColumn="online">
                        Status
                        <p-sorticon field="online" />
                      </th>
                      <th pSortableColumn="name">
                        Name
                        <p-sorticon field="name" />
                      </th>
                      <th pSortableColumn="manufacturerName">
                        Manufacturer
                        <p-sorticon field="manufacturerName" />
                      </th>
                      <th pSortableColumn="modelName">
                        Model
                        <p-sorticon field="modelName" />
                      </th>
                      <th>{{ controlSystemLabel }}</th>
                      <th pSortableColumn="lastSeenUtc">
                        Last Seen
                        <p-sorticon field="lastSeenUtc" />
                      </th>
                    </tr>
                  </ng-template>
                  <ng-template #body let-device>
                    <tr
                      class="device-row"
                      tabindex="0"
                      [attr.aria-label]="'Open ' + linkedDisplayName(device)"
                      (click)="navigateToLinkedDevice(device)"
                      (keydown.enter)="navigateToLinkedDevice(device)"
                      (contextmenu)="showContextMenu($event, device, linkedDeviceMenu)"
                    >
                      <td>
                        <app-status-badge [status]="linkedStatus(device)" size="sm" />
                      </td>
                      <td>
                        <div class="device-name">{{ linkedDisplayName(device) }}</div>
                        @if (linkedProblem(device); as problem) {
                          <div class="device-problem" [title]="problem">{{ problem }}</div>
                        }
                      </td>
                      <td>{{ device.manufacturerName || '—' }}</td>
                      <td>{{ device.modelName || '—' }}</td>
                      <td>
                        <a *ngIf="device.parentDeviceId" [routerLink]="['/device', device.parentDeviceId]" class="device-link device-link-small" (click)="$event.stopPropagation()">
                          {{ getParentDeviceName(device.parentDeviceId) }}
                        </a>
                        <span *ngIf="!device.parentDeviceId" class="text-secondary">-</span>
                      </td>
                      <td>
                        {{ formatDeviceLastSeen(device) }}
                      </td>
                    </tr>
                  </ng-template>
                </p-table>
              </div>
              @if (equipmentSummary().total === 0) {
                <app-empty-state icon="pi pi-desktop" [title]="t()('room.noDevices')" [description]="t()('room.noDevicesDetail')" />
              }
            }
          </p-tabpanel>
          <p-tabpanel value="documents">
            @if (activeTab() === 'documents') {
              <app-room-content kind="documents" [roomId]="location()!.id" [namespaceId]="location()!.appNamespaceId" [sharePointUrl]="location()!.sharePointUrl" />
            }
          </p-tabpanel>
          <p-tabpanel value="notes">
            @if (activeTab() === 'notes') {
              <app-room-content
                kind="notes"
                [roomId]="location()!.id"
                [namespaceId]="location()!.appNamespaceId"
                [draft]="roomDraft().roomId === location()!.id ? roomDraft().body : ''"
                (draftChange)="roomDraft.set({ roomId: location()!.id, body: $event })"
              />
            }
          </p-tabpanel>
          <p-tabpanel value="settings">
            @if (activeTab() === 'settings') {
              <app-location-settings [locationId]="location()!.id" [locationName]="locationName()" [sharePointUrl]="location()!.sharePointUrl" [canManage]="canManageLocations()" [isRoom]="location()!.type === 'room'" (sharePointUrlChange)="onSharePointUrlChange($event)" />
            }
          </p-tabpanel>
        </p-tabpanels>
      </p-tabs>
    </div>

    <app-room-profile-dialog [(visible)]="roomProfileVisible" [room]="roomProfileTarget()" />
    @if (canManageIssues() && location()) {
      <app-create-issue-dialog [(visible)]="reportIssueVisible" [locationId]="location()!.id" [locationName]="locationName()" [namespaceId]="location()!.appNamespaceId" (created)="onIssueCreated()" />
    }

    <!-- Context Menu for Linked Devices -->
    <p-contextmenu #linkedDeviceMenu [model]="linkedDeviceContextMenuItems"></p-contextmenu>

    <!-- Move Linked Device Dialog -->
    <p-dialog [visible]="showMoveDialog()" (visibleChange)="showMoveDialog.set($event)" header="Move Linked Device" [modal]="true" [closable]="false" [style]="{ width: '40rem', maxHeight: '80vh' }">
      <div class="move-dialog-content" *ngIf="selectedLinkedDevice() as child">
        <div class="p-3 bg-surface-100 dark:bg-surface-800 rounded mb-3">
          <div class="text-sm text-color-secondary mb-1">Device</div>
          <div class="font-semibold">{{ linkedDisplayName(child) }}</div>
          <div class="text-xs text-color-secondary font-mono">{{ child.uniqueId }}</div>
        </div>

        <p class="text-xs text-color-secondary mb-2">Select a new location from the tree below.</p>

        <app-location-picker
          [locations]="availableLocations()"
          [namespaceId]="location()?.appNamespaceId ?? null"
          [currentLocationId]="location()?.id"
          [minHeight]="'300px'"
          [maxHeight]="'400px'"
          (locationSelected)="selectedTargetLocation.set($event)"
        />

        <!-- Selected location confirmation -->
        <div *ngIf="selectedTargetLocation()" class="mt-3 p-3 bg-primary-50 dark:bg-primary-900 border border-primary-200 dark:border-primary-700 rounded flex items-center gap-2">
          <i class="pi pi-check-circle text-primary"></i>
          <div>
            <div class="text-xs text-color-secondary">Selected location:</div>
            <div class="font-semibold">{{ getLocationName(selectedTargetLocation()!) }}</div>
          </div>
        </div>

        <p-message *ngIf="moveError()" severity="error" class="mt-3">{{ moveError()! }}</p-message>
      </div>
      <ng-template #footer>
        <div class="flex justify-end gap-2">
          <button pButton type="button" severity="secondary" (click)="showMoveDialog.set(false)" [disabled]="moveSubmitting()"><span class="p-button-label">Cancel</span></button>
          <button pButton type="button" (click)="confirmMoveLinkedDevice()" [disabled]="!selectedTargetLocation() || moveSubmitting()" [loading]="moveSubmitting()">
            @if (moveSubmitting()) {
              <i class="pi pi-spinner pi-spin p-button-loading-icon" aria-hidden="true"></i>
            }
            <span class="p-button-label">Move</span>
          </button>
        </div>
      </ng-template>
    </p-dialog>

    <!-- Remove Linked Device Dialog -->
    <p-dialog [visible]="showRemoveDialog()" (visibleChange)="showRemoveDialog.set($event)" header="Remove Linked Device" [modal]="true" [closable]="false" [style]="{ width: '440px' }">
      <div class="flex flex-col gap-3" *ngIf="removeTarget() as dev">
        <p class="m-0 text-sm">
          Are you sure you want to remove <strong>{{ linkedDisplayName(dev) }}</strong> from the linked devices list?
        </p>
        <p class="m-0 text-xs text-color-secondary">This device is no longer being reported by the parent processor. It will reappear automatically if it comes back online.</p>
        <div class="text-xs text-status-offline" *ngIf="removeError()">{{ removeError() }}</div>
      </div>
      <ng-template #footer>
        <div class="flex justify-end gap-2">
          <button pButton type="button" severity="secondary" (click)="showRemoveDialog.set(false)" [disabled]="removeSubmitting()"><span class="p-button-label">Cancel</span></button>
          <button pButton type="button" severity="danger" (click)="confirmRemoveLinkedDevice()" [disabled]="removeSubmitting()" [loading]="removeSubmitting()">
            @if (removeSubmitting()) {
              <i class="pi pi-spinner pi-spin p-button-loading-icon" aria-hidden="true"></i>
            } @else {
              <i class="pi pi-trash p-button-icon" aria-hidden="true"></i>
            }
            <span class="p-button-label">Remove</span>
          </button>
        </div>
      </ng-template>
    </p-dialog>
  `,
  styleUrls: ['./location-details.component.scss']
})
export class LocationDetailsComponent implements OnInit, OnDestroy, OnChanges {
  private cloudApi = inject(CloudApiService);
  private route = inject(ActivatedRoute);
  private messageService = inject(MessageService);
  private router = inject(Router);
  private queryParams = toSignal(this.route.queryParamMap, { initialValue: this.route.snapshot.queryParamMap });
  readonly roomDraft = signal({ roomId: '', body: '' });
  private static readonly TABS = ['overview', 'ui-control', 'devices', 'usage', 'power', 'feedback', 'documents', 'notes', 'settings'] as const;
  readonly activeTab = computed(() => {
    const tab = this.queryParams().get('tab');
    // Links from before the Insights tab was split into Room Usage and Power.
    if (tab === 'insights') return 'usage';
    return LocationDetailsComponent.isTab(tab) ? tab : 'overview';
  });

  private static isTab(tab: unknown): tab is (typeof LocationDetailsComponent.TABS)[number] {
    return typeof tab === 'string' && (LocationDetailsComponent.TABS as readonly string[]).includes(tab);
  }

  changeTab(tab: string | number | undefined): void {
    if (!LocationDetailsComponent.isTab(tab)) return;
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: tab === 'overview' ? null : tab },
      queryParamsHandling: 'merge'
    });
  }

  onSharePointUrlChange(url: string | null): void {
    this.location.update((current) => current ? { ...current, sharePointUrl: url } : current);
  }

  // Can accept locationId as input (for dashboard integration) or from route params
  @Input() locationId: string | null = null;
  readonly loadStateChange = output<{ locationId: string; loading: boolean }>();

  location = signal<any | null>(null);
  loading = signal(true);
  private insightPanels = inject(INSIGHT_PANELS, { optional: true }) ?? [];
  /** Equipment in this room with capabilities (from /api/linked-devices) - decides which insight panels apply. */
  readonly roomLinkedDevices = signal<InsightLinkedDevice[] | null>(null);
  readonly insightTabs = INSIGHT_TABS;
  /** Room Usage / Power tabs with at least one panel that has data for this room. */
  readonly availableInsightTabs = computed<InsightTabDef[]>(() => {
    const loc = this.location();
    const devices = this.roomLinkedDevices();
    if (!loc || !devices) return [];
    const panels = availableInsightPanels(this.insightPanels, { scope: 'location', id: loc.id, namespaceId: loc.appNamespaceId, linkedDevices: devices, location: this.currentLocationFromArray() ?? loc });
    return INSIGHT_TABS.filter((tab) => panels.some((p) => p.tab === tab.id));
  });
  /** Tabs in the tab bar: the available ones, plus the open one so a deep link keeps its tab. */
  readonly visibleInsightTabs = computed<InsightTabDef[]>(() => {
    const available = this.availableInsightTabs();
    const active = this.activeTab();
    return INSIGHT_TABS.filter((tab) => available.includes(tab) || tab.id === active);
  });
  readonly t = inject(TranslationService).t;
  readonly loadError = signal<string | null>(null);
  private locationRequest = 0;
  readonly emptyLocation = computed(() => {
    const location = this.location();
    return !!location && !this.loading() && !this.loadError() && !location.parentDevices?.length && !location.linkedDevices?.length;
  });
  private activeLocationId = signal<string | null>(null);
  private routeSubscription?: Subscription;
  private linkedDeviceSyncSub?: Subscription;
  readonly controlSystemId = computed<string | null>(() => {
    const location = this.location();
    return location?.controlSystemDeviceId ?? this.currentLocationFromArray()?.controlSystemDeviceId ?? (location?.parentDevices?.length === 1 ? location.parentDevices[0].id : null);
  });
  readonly equipmentSummary = computed(() => {
    const devices: { online?: boolean; missing?: boolean }[] = this.location()?.linkedDevices ?? [];
    const online = devices.filter((device) => device.online && !device.missing).length;
    return { total: devices.length, online, attention: devices.length - online };
  });

  // Generate unique state key for linked devices table based on location ID
  linkedDevicesTableStateKey = computed(() => {
    const locId = this.activeLocationId();
    return locId ? `linkedDevicesInRoom_${locId}` : 'linkedDevicesInRoom_default';
  });

  // Ticking clock for live relative time updates
  now = signal(Date.now());
  private tick = setInterval(() => this.now.set(Date.now()), 1000);

  // Live parent device: merges real-time WebSocket data from devices() into
  // the first parentDevice from the location details snapshot.
  parentDevice = computed(() => {
    const loc = this.location();
    const ownerId = this.controlSystemId();
    const base = ownerId ? loc?.parentDevices?.find((device: { id: string }) => device.id === ownerId) : loc?.parentDevices?.[0];
    if (!base) return null;
    const live = this.cloudApi.devices()?.find((d: any) => d.id === base.id);
    return live ? { ...base, ...live } : base;
  });

  /** Health of this location's system, re-evaluated as the clock ticks so a silent processor turns Offline. */
  parentStatus = computed<StatusDescriptor>(() => deriveDeviceStatus(this.parentDevice(), this.now()));

  linkedStatus = deriveLinkedDeviceStatus;
  linkedProblem = linkedDeviceProblem;

  /** Live occupancy / booking state, patched in place in the locations list by the usage WebSocket topic. */
  readonly roomUsage = computed<RoomUsageState | null>(() => {
    const fromList = this.currentLocationFromArray();
    return fromList ? (fromList.usage ?? null) : (this.location()?.usage ?? null);
  });
  readonly isRoom = computed(() => (this.currentLocationFromArray()?.type ?? this.location()?.type) === 'room');
  /** Capacity (effective and cloud-set) and room type, from the live locations list when loaded. */
  readonly roomProfile = computed(() => {
    const loc: LocationDto | null = this.currentLocationFromArray() ?? this.location();
    const usage = this.roomUsage();
    return {
      capacity: effectiveCapacity({ capacity: loc?.capacity, usage }),
      cloudCapacity: typeof loc?.capacity === 'number' ? loc.capacity : null,
      roomType: isRoomType(loc?.roomType) ? loc.roomType : null
    };
  });
  readonly roomSeats = computed(() => translateCount(this.t(), 'roomCapacity.seats', this.roomProfile().capacity));
  readonly canManageLocations = computed(() => {
    const ns = this.location()?.appNamespaceId ?? this.currentLocationFromArray()?.appNamespaceId;
    return this.cloudApi.hasCapability?.(ns, 'locations.manage') ?? false;
  });
  /** The user may see / raise GitHub issues for this room (issues.view / issues.manage). */
  readonly canViewIssues = computed(() => {
    const ns = this.location()?.appNamespaceId ?? this.currentLocationFromArray()?.appNamespaceId;
    return this.cloudApi.hasCapability?.(ns, 'issues.view') ?? false;
  });
  readonly canManageIssues = computed(() => {
    const ns = this.location()?.appNamespaceId ?? this.currentLocationFromArray()?.appNamespaceId;
    return this.cloudApi.hasCapability?.(ns, 'issues.manage') ?? false;
  });
  readonly reportIssueVisible = signal(false);
  readonly roomIssues = signal<RoomIssuesResponse | null>(null);
  readonly roomIssuesLoading = signal(false);
  readonly openRoomIssues = computed<IssueListItem[]>(() => (this.roomIssues()?.issues ?? []).filter((i) => i.state !== 'closed'));
  readonly roomIssueFailures = computed<IssueRepositoryFailure[]>(() => this.roomIssues()?.partialFailures ?? []);
  private roomIssuesRequest = 0;
  private readonly roomIssuesEffect = effect(() => {
    const locId = this.activeLocationId();
    const canView = this.canViewIssues();
    if (locId && canView) void this.loadRoomIssues(locId);
    else this.roomIssues.set(null);
  });
  private issueEventsSub?: Subscription;

  readonly roomProfileVisible = signal(false);
  readonly roomProfileTarget = computed<RoomProfileTarget | null>(() => {
    const loc = this.location();
    if (!loc) return null;
    const profile = this.roomProfile();
    return { id: loc.id, name: this.locationName(), capacity: profile.cloudCapacity, roomType: profile.roomType, sensorCapacity: this.roomUsage()?.capacity ?? null };
  });

  openRoomProfile(): void {
    this.roomProfileVisible.set(true);
  }

  openReportIssue(): void {
    this.reportIssueVisible.set(true);
  }

  onIssueCreated(): void {
    const locId = this.activeLocationId();
    if (locId) void this.loadRoomIssues(locId);
  }

  issueLink(issue: IssueListItem): string[] {
    return issueRoute(issue.repository, issue.number);
  }

  visibleIssueLabels(issue: IssueListItem) {
    return issue.labels.filter((l) => !isRoomLabel(l.name)).slice(0, 3);
  }

  issueLabelTextColor(color: string | null): string {
    return labelTextColor(color || 'ededed');
  }

  roomIssueFailureText(failure: IssueRepositoryFailure): string {
    return repositoryFailureText(this.t(), failure);
  }

  /** The room's open GitHub issues for the Overview widget; tolerant of an API stub without the call. */
  async loadRoomIssues(locationId: string): Promise<void> {
    const request = ++this.roomIssuesRequest;
    const call = this.cloudApi.listLocationIssues;
    if (typeof call !== 'function') return;
    this.roomIssuesLoading.set(true);
    try {
      const result = await call.call(this.cloudApi, locationId, false);
      if (request === this.roomIssuesRequest && this.activeLocationId() === locationId) this.roomIssues.set(result);
    } catch {
      if (request === this.roomIssuesRequest) this.roomIssues.set(null);
    } finally {
      if (request === this.roomIssuesRequest) this.roomIssuesLoading.set(false);
    }
  }

  /** Usage is the last reported state, not live, while the room's control system is not online. */
  readonly usageStale = computed(() => !!this.roomUsage() && this.parentStatus().key !== 'online');

  // ---- Overview snippets: one small insight per statistics tab -------------------------------
  private readonly injector = inject(Injector);
  /** Live room status key for the usage snippet when no utilisation history exists yet. */
  readonly liveRoomStatus = computed(() => roomStatus(this.roomUsage()));
  /** Occupancy KPIs over the last 7 days; `undefined` while loading, `null` when unavailable. */
  readonly overviewUsage = signal<OccupancyKpis | null | undefined>(null);
  /** Feedback average and response count over the last 30 days; `undefined` while loading, `null` when unavailable. */
  readonly overviewFeedback = signal<{ average: number | null; count: number } | null | undefined>(null);
  readonly feedbackStars = computed(() => {
    const average = this.overviewFeedback()?.average;
    return average == null ? '' : starGlyphs(average);
  });
  readonly feedbackResponses = computed(() => translateCount(this.t(), 'room.snippet.responses', this.overviewFeedback()?.count ?? 0));
  private readonly powerDevices = computed(() => (this.roomLinkedDevices() ?? []).filter((d) => !!d.capabilities?.['hasOutlets']));
  /** Current draw of the room's PDUs from their last reported state (the Power tab streams live values). */
  readonly powerNowWatts = computed<number | null>(() => {
    const totals = this.powerDevices()
      .map((d) => (d.state as { total?: { watts?: number | null } | null } | null | undefined)?.total?.watts)
      .filter((w): w is number => typeof w === 'number' && Number.isFinite(w));
    return totals.length ? totals.reduce((a, b) => a + b, 0) : null;
  });
  readonly powerDeviceCount = computed(() => translateCount(this.t(), 'room.snippet.pdus', this.powerDevices().length));
  private overviewRequest = 0;
  private overviewKey: string | null = null;
  private readonly overviewSnippetsEffect = effect(() => {
    const locId = this.activeLocationId();
    const loc = this.location();
    const tabs = this.availableInsightTabs().map((tab) => tab.id);
    untracked(() => {
      if (!loc || !locId || loc.id !== locId) return;
      // Live usage updates recompute the available tabs; only a new room or a new tab set refetches.
      const key = `${locId}|${tabs.join(',')}`;
      if (key === this.overviewKey) return;
      this.overviewKey = key;
      void this.loadOverviewSnippets(locId, tabs);
    });
  });

  /** Loads the Overview snippets' history (7-day utilisation, 30-day feedback) for the tabs the room has. */
  async loadOverviewSnippets(locationId: string, tabs: InsightTab[]): Promise<void> {
    const request = ++this.overviewRequest;
    const to = new Date();
    const day = 86_400_000;
    const current = () => request === this.overviewRequest && this.activeLocationId() === locationId;

    const metrics = tabs.includes('usage') ? this.resolveService(CloudMetricsService) : null;
    this.overviewUsage.set(metrics ? undefined : null);
    if (metrics) {
      metrics
        .getLocationMetrics(locationId, { type: 'occupancy', from: new Date(to.getTime() - 7 * day), to, bucketMinutes: 1440, includeBreakdown: false, workingHours: true })
        .then((result) => current() && this.overviewUsage.set(occupancyKpis(result.summary)))
        .catch(() => current() && this.overviewUsage.set(null));
    }

    const feedback = tabs.includes('feedback') ? this.resolveService(CloudFeedbackService) : null;
    this.overviewFeedback.set(feedback ? undefined : null);
    if (feedback) {
      feedback
        .getSummary('locations', locationId, new Date(to.getTime() - 30 * day), to, 1440)
        .then((summary) => current() && this.overviewFeedback.set({ average: summary.average, count: summary.count }))
        .catch(() => current() && this.overviewFeedback.set(null));
    }
  }

  /** The insight services need HTTP; a host without it (an API stub in tests) just gets no snippet history. */
  private resolveService<T>(token: ProviderToken<T>): T | null {
    try {
      return this.injector.get(token, null);
    } catch {
      return null;
    }
  }

  // Computed signal that tracks the current location from the locations array
  // This allows us to detect changes to the specific location being viewed
  readonly currentLocationFromArray = computed<LocationDto | null>(() => {
    const locId = this.activeLocationId();
    const locations = this.cloudApi.locations();
    if (!locId || !locations) return null;
    return locations.find((l) => l.id === locId) ?? null;
  });

  // Effect to load location whenever the active location ID changes
  private loadEffect = effect(() => {
    const locId = this.activeLocationId();
    if (locId) {
      this.loadLocation(locId);
    }
  });

  // Effect to reload location details when the specific location in the array changes
  // This happens when the location is updated via WebSocket
  private reloadOnSpecificLocationChange = effect(() => {
    const locFromArray = this.currentLocationFromArray();
    const currentLoc = this.location();

    // Only reload if:
    // 1. We have a location loaded
    // 2. We detected a change in the location from the array (name, etc changed)
    // 3. The location name actually changed (simple dirty check)
    if (currentLoc && locFromArray && currentLoc.name !== locFromArray.name) {
      const locId = this.activeLocationId();
      if (locId) {
        this.loadLocation(locId);
      }
    }
  });

  ngOnChanges(changes: SimpleChanges) {
    // Handle input changes (when used in dashboard with @Input binding)
    if (changes['locationId'] && changes['locationId'].currentValue) {
      this.activeLocationId.set(changes['locationId'].currentValue);
    }
  }

  ngOnInit() {
    // Issues raised or changed for this room (over the WebSocket) refresh the Overview widget.
    this.issueEventsSub = this.cloudApi.issueEvents$?.subscribe((event) => {
      const locId = this.activeLocationId();
      if (locId && event.locationId === locId) void this.loadRoomIssues(locId);
    });

    // Handle initial input value
    if (this.locationId) {
      this.activeLocationId.set(this.locationId);
      return;
    }

    // Subscribe to route params changes - using paramMap for better change detection
    this.routeSubscription = this.route.paramMap.subscribe((params) => {
      const idFromRoute = params.get('id');
      if (idFromRoute) {
        this.activeLocationId.set(idFromRoute);
      }
    });
  }

  ngOnDestroy() {
    this.locationRequest++;
    this.roomIssuesRequest++;
    this.overviewRequest++;
    this.issueEventsSub?.unsubscribe();
    this.routeSubscription?.unsubscribe();
    this.linkedDeviceSyncSub?.unsubscribe();
    clearInterval(this.tick);
  }

  async loadLocation(locationId: string) {
    const request = ++this.locationRequest;
    this.loadStateChange.emit({ locationId, loading: true });
    this.loadError.set(null);
    this.location.set(null);
    this.roomLinkedDevices.set(null);
    this.linkedDeviceSyncSub?.unsubscribe();
    try {
      this.loading.set(true);
      const data = await this.cloudApi.getLocationDetails(locationId);
      if (request !== this.locationRequest) return;
      this.location.set(data);
      void this.loadRoomLinkedDevices(request, data, locationId);

      // Subscribe to linked-device sync events for the parent device
      this.linkedDeviceSyncSub?.unsubscribe();
      const parentId = data?.parentDevices?.[0]?.id;
      if (parentId) {
        this.linkedDeviceSyncSub = this.cloudApi.subscribeToLinkedDeviceSync(parentId).subscribe((event) => {
          if (!event) return;
          const loc = this.location();
          if (!loc?.appNamespaceId) return;
          this.cloudApi
            .getAllLinkedDevices(loc.appNamespaceId, locationId)
            .then((fresh) => {
              const currentLoc = this.location();
              if (!currentLoc || currentLoc !== loc) return;
              const current: any[] = currentLoc.linkedDevices ?? [];
              const freshMap = new Map((fresh || []).map((d: any) => [d.id, d]));
              const currentMap = new Map(current.map((d: any) => [d.id, d]));
              const hasStructuralChange = current.length !== (fresh?.length ?? 0) || (fresh || []).some((d: any) => !currentMap.has(d.id)) || current.some((d: any) => !freshMap.has(d.id));
              let updatedDevices: any[];
              if (hasStructuralChange) {
                updatedDevices = fresh || [];
              } else {
                let anyChange = false;
                updatedDevices = current.map((existing: any) => {
                  const f = freshMap.get(existing.id);
                  if (!f) return existing;
                  if (
                    existing.online !== f.online ||
                    existing.missing !== f.missing ||
                    existing.lastSeenUtc !== f.lastSeenUtc ||
                    existing.state?.powerStatus !== f.state?.powerStatus ||
                    existing.state?.currentInput !== f.state?.currentInput ||
                    existing.state?.inStandby !== f.state?.inStandby
                  ) {
                    anyChange = true;
                    return { ...existing, ...f };
                  }
                  return existing;
                });
                if (!anyChange) return;
              }
              this.location.set({ ...currentLoc, linkedDevices: updatedDevices });
            })
            .catch((err) => console.debug('Silent linked-device refresh failed', err));
        });
      }
    } catch (e: unknown) {
      if (request !== this.locationRequest) return;
      this.loadError.set(e instanceof Error ? e.message : 'Failed to load location details');
      console.error('Failed to load location details', e);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to load location details'
      });
    } finally {
      if (request === this.locationRequest) {
        this.loading.set(false);
        this.loadStateChange.emit({ locationId, loading: false });
      }
    }
  }

  private async loadRoomLinkedDevices(request: number, data: { appNamespaceId?: string } | null, locationId: string): Promise<void> {
    if (!data?.appNamespaceId) {
      this.roomLinkedDevices.set([]);
      return;
    }
    try {
      const devices = await this.cloudApi.getAllLinkedDevices(data.appNamespaceId, locationId);
      if (request === this.locationRequest) this.roomLinkedDevices.set(devices ?? []);
    } catch {
      if (request === this.locationRequest) this.roomLinkedDevices.set([]);
    }
  }

  formatDate(dateStr: string | null | undefined): string {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleString();
    } catch {
      return dateStr || '-';
    }
  }

  formatUptime(seconds: number | null | undefined): string {
    if (!seconds) return '-';
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  }

  formatRelativeTime(dateStr: string | Date | null | undefined, nowTs?: number): string {
    if (!dateStr) return '-';
    let t: number;
    if (dateStr instanceof Date) {
      t = dateStr.getTime();
    } else {
      // Server timestamps are always UTC. If the string lacks a timezone
      // suffix (Z or +offset), append 'Z' so the browser doesn't treat it
      // as local time (which causes a 1-hour error during DST).
      let s = dateStr;
      if (!/[Zz]|[+-]\d{2}:\d{2}$/.test(s)) {
        s += 'Z';
      }
      t = new Date(s).getTime();
    }
    if (isNaN(t)) return '-';
    const now = nowTs ?? this.now();
    let diff = Math.max(0, Math.floor((now - t) / 1000));
    if (diff < 60) return `${diff}s ago`;
    const minutes = Math.floor(diff / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  }

  private getDeviceLastSeenTimestamp(device: any): string | null {
    return device?.lastSeenUtc ?? device?.lastCheckInUtc ?? null;
  }

  formatDeviceLastSeen(device: any): string {
    if (device?.missing) return 'Missing';
    const ts = this.getDeviceLastSeenTimestamp(device);
    if (!ts) return 'Never seen';
    if (device?.online) return 'Online now';
    return this.formatRelativeTime(ts, this.now());
  }

  /** What the portal calls the processor a linked device hangs off. */
  readonly controlSystemLabel = CONTROL_SYSTEM_LABEL;

  /** Template helper — see linkedDeviceDisplayName for the precedence. */
  linkedDisplayName = linkedDeviceDisplayName;

  /** Human label for this node's type — "Room", "Floor", "Building", else "Location". */
  typeLabel = computed(() => locationTypeLabel(this.location()?.type));

  /** This location's name, never its id. */
  locationName = computed(() => locationDisplayName(this.location()?.name, this.location()?.type));

  /** The single control system this location belongs to. */
  parentDeviceName = computed(() => {
    const parent = this.parentDevice();
    return controlSystemDisplayName(parent ? deviceDisplayName(parent) : null, parent?.id);
  });

  getParentDeviceName(parentDeviceId: string): string {
    const parentDevices = this.location()?.parentDevices || [];
    const parent = parentDevices.find((d: any) => d.id === parentDeviceId);
    return controlSystemDisplayName(parent ? deviceDisplayName(parent) : null, parentDeviceId);
  }

  // Context menu and move linked device functionality
  showMoveDialog = signal(false);
  selectedLinkedDevice = signal<any>(null);
  selectedTargetLocation = signal<string | null>(null);
  moveError = signal<string | null>(null);
  moveSubmitting = signal(false);

  getLocationName(locationId: string): string {
    const locations = this.availableLocations();
    const location = locations.find((l: any) => l.id === locationId);
    return locationDisplayName(location?.name, location?.type);
  }

  linkedDeviceContextMenuItems: MenuItem[] = [
    {
      label: 'Move to Location',
      icon: 'pi pi-arrow-right',
      command: () => this.openMoveDialog(this.selectedLinkedDevice())
    },
    {
      label: 'Remove Device',
      icon: 'pi pi-trash',
      visible: false,
      command: () => this.openRemoveDialog(this.selectedLinkedDevice())
    }
  ];

  navigateToLinkedDevice(device: any) {
    if (device?.id) {
      this.router.navigate(['/linked-device', device.id]);
    }
  }

  showContextMenu(event: MouseEvent, device: any, menu: ContextMenu) {
    event.preventDefault();
    event.stopPropagation();
    this.selectedLinkedDevice.set(device);
    // Update Remove Device visibility based on missing status
    this.linkedDeviceContextMenuItems = this.linkedDeviceContextMenuItems.map((mi) => ({
      ...mi,
      visible: mi.label === 'Remove Device' ? !!device.missing : true
    }));
    menu.show(event);
  }

  availableLocations = computed(() => {
    const currentLocation = this.location();
    if (!currentLocation) return [];

    // Get all locations from the cloud API
    const allLocations = this.cloudApi.locations() || [];

    // Filter out the current location and show only locations in the same namespace
    return allLocations.filter((loc: any) => loc.appNamespaceId === currentLocation.appNamespaceId && loc.id !== currentLocation.id);
  });

  openMoveDialog(linkedDevice: any) {
    this.selectedLinkedDevice.set(linkedDevice);
    this.selectedTargetLocation.set(null);
    this.moveError.set(null);
    this.showMoveDialog.set(true);
  }

  async confirmMoveLinkedDevice() {
    const linkedDevice = this.selectedLinkedDevice();
    const targetLocationId = this.selectedTargetLocation();

    if (!linkedDevice || !linkedDevice.id || !targetLocationId) {
      this.moveError.set('Please select a target location');
      return;
    }

    this.moveSubmitting.set(true);
    this.moveError.set(null);

    try {
      await this.cloudApi.moveLinkedDevice(linkedDevice.id, targetLocationId);

      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: `Linked device "${linkedDevice.name}" moved successfully`
      });

      // Reload location details to reflect changes
      const currentLocId = this.activeLocationId();
      if (currentLocId) {
        await this.loadLocation(currentLocId);
      }

      this.showMoveDialog.set(false);
    } catch (error: any) {
      console.error('Failed to move linked device:', error);
      this.moveError.set(error?.error?.detail || 'Failed to move linked device');
    } finally {
      this.moveSubmitting.set(false);
    }
  }

  // Remove linked device dialog
  showRemoveDialog = signal(false);
  removeTarget = signal<any>(null);
  removeSubmitting = signal(false);
  removeError = signal<string | null>(null);

  openRemoveDialog(linkedDevice: any) {
    this.removeTarget.set(linkedDevice);
    this.removeError.set(null);
    this.showRemoveDialog.set(true);
  }

  async confirmRemoveLinkedDevice() {
    const linkedDevice = this.removeTarget();
    if (!linkedDevice?.id) return;

    this.removeSubmitting.set(true);
    this.removeError.set(null);

    try {
      await this.cloudApi.deleteLinkedDevice(linkedDevice.id);
      const currentLocId = this.activeLocationId();
      if (currentLocId) {
        await this.loadLocation(currentLocId);
      }
      this.showRemoveDialog.set(false);
      this.removeTarget.set(null);
    } catch (err: any) {
      console.error('Failed to remove linked device', err);
      this.removeError.set(err?.error?.detail || err?.message || 'Failed to remove linked device');
    } finally {
      this.removeSubmitting.set(false);
    }
  }

  goBack() {
    this.router.navigate(['/dashboard']);
  }
}
